import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReasoningEffort, SessionSettings } from '../../services/chat-execution';
import { ComposerModelOption, composerEffortPlaceholder } from './composer-model-options.component';
import { BrIconComponent } from '../../shared/ui/icon/icon.component';

export interface ConversationAgentOption { key?: string; name: string; description: string; icon: string; }

/** UI placeholders until a permission-scoped agent directory is available. */
export const conversationAgentPlaceholders: readonly ConversationAgentOption[] = [
  { key: 'bree', name: 'BRee', description: 'Handle requests and coordinate work across agents', icon: 'auto_awesome' },
  { key: 'scout', name: 'Scout', description: 'Find and enrich places', icon: 'place' },
  { key: 'route', name: 'Route', description: 'Plan routes and visits', icon: 'route' },
  { key: 'atlas', name: 'Atlas', description: 'Research markets and competitors', icon: 'search' },
];

@Component({
  selector: 'app-conversation-agents',
  standalone: true,
  imports: [CommonModule, FormsModule, BrIconComponent],
  template: `
    <header>
      <h2>Agents</h2>
      <button type="button" class="br-btn br-btn--ghost br-btn--sm"
              aria-label="Back to conversation" (click)="returnToConversation.emit()">
        <br-icon aria-hidden="true">arrow_back</br-icon> Back
      </button>
    </header>
    <div class="agents-scroll">
      <p class="agents-hint br-small br-text-muted">Mention an agent to add it to your message.
        <span>Model and effort follow the composer unless customized.</span>
      </p>
      <section *ngFor="let agent of agents" class="agent-entry">
        <br-icon class="agent-icon" aria-hidden="true">{{ agent.icon }}</br-icon>
        <div class="agent-info"><strong>{{ agent.name }}</strong><small>{{ agent.description }}</small></div>
        <button type="button" class="br-btn br-btn--ghost br-btn--sm br-interactive agent-mention"
                (click)="mention.emit(agent)" [attr.aria-label]="'Mention ' + agent.name">
          <span aria-hidden="true">&#64;</span> Mention
        </button>
        <span *ngIf="agent.key === 'bree'" class="agent-default">{{ defaultModelLabel }} · {{ defaultEffortLabel }}</span>
        <details *ngIf="agent.key && agent.key !== 'bree'" class="agent-options">
          <summary class="br-interactive" [attr.aria-label]="agent.name + ' model and effort settings'">
            <span class="agent-settings-summary">{{ modelLabel(agent.key) }} · {{ effortLabel(agent.key) }}</span>
            <span *ngIf="setting(agent.key).model || setting(agent.key).effort" class="agent-custom">Custom</span>
            <br-icon aria-hidden="true">expand_more</br-icon>
          </summary>
          <div class="agent-settings">
            <label><span>Model</span>
              <select class="br-form-select br-form-select--sm" [attr.aria-label]="agent.name + ' model'"
                      [ngModel]="setting(agent.key).model" (ngModelChange)="changeModel(agent.key, $event)">
                <option [ngValue]="null">Follow composer · {{ defaultModelLabel }}</option>
                <option *ngFor="let model of models" [ngValue]="model.model || model.id">{{ model.label || model.model }}</option>
                <option *ngIf="hasCustomModel(agent.key)" [ngValue]="setting(agent.key).model">{{ setting(agent.key).model }}</option>
              </select>
            </label>
            <label><span>Effort</span>
              <select class="br-form-select br-form-select--sm" [attr.aria-label]="agent.name + ' effort'"
                      [ngModel]="setting(agent.key).effort" (ngModelChange)="changeEffort(agent.key, $event)">
                <option [ngValue]="null">Follow composer · {{ defaultEffortLabel }}</option>
                <option *ngFor="let effort of efforts" [ngValue]="effort.value">{{ effort.label }}</option>
              </select>
            </label>
            <button *ngIf="setting(agent.key).model || setting(agent.key).effort" type="button"
                    class="br-btn br-btn--ghost br-btn--sm reset" (click)="reset(agent.key)">Use defaults</button>
          </div>
        </details>
      </section>
    </div>`,
  styleUrls: ['./conversation-scrollbar.css'],
  styles: [`
    :host { display: flex; flex: 1; min-height: 0; flex-direction: column; color: var(--br-color-text); }
    header { display: flex; align-items: center; gap: var(--br-space-2); padding: 0 var(--br-space-4) var(--br-space-2); }
    h2 { margin: 0; font-size: var(--br-font-size-md); font-weight: 600; }
    header button { margin-left: auto; }
    .agents-scroll { overflow-y: auto; min-height: 0; padding: 0 var(--br-space-4) var(--br-space-4); }
    .agents-hint { margin: var(--br-space-1) 0 var(--br-space-3); line-height: var(--br-line-height-body); }
    .agents-hint span { display: block; }
    .agent-entry { display: grid; grid-template-columns: 1.125rem minmax(0, 1fr) auto; column-gap: var(--br-space-3); row-gap: var(--br-space-1); padding: var(--br-space-3) 0; border-bottom: 1px solid var(--br-color-border-soft); }
    .agent-entry:last-child { border-bottom: 0; }
    .agent-icon { width: 1.125rem; height: 1.125rem; font-size: 1.125rem; margin-top: var(--br-space-1); color: var(--br-color-muted); }
    .agent-info { min-width: 0; }
    strong { display: block; font-size: var(--br-font-size-sm); font-weight: 600; line-height: var(--br-line-height-body); }
    small { display: block; font-size: var(--br-font-size-xs); line-height: var(--br-line-height-body); color: var(--br-color-muted); }
    .agent-mention { align-self: start; }
    .agent-default, .agent-options { grid-column: 2 / -1; min-width: 0; font-size: var(--br-font-size-xs); line-height: var(--br-line-height-body); color: var(--br-color-muted); }
    summary { display: flex; align-items: center; gap: var(--br-space-1); width: fit-content; max-width: 100%; border-radius: var(--br-radius-sm); cursor: pointer; list-style: none; }
    summary::-webkit-details-marker { display: none; }
    .agent-settings-summary { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    summary br-icon { width: 1rem; height: 1rem; font-size: 1rem; flex-shrink: 0; }
    details[open] summary br-icon { transform: rotate(180deg); }
    .agent-custom { margin-left: var(--br-space-1); }
    .agent-settings { display: flex; align-items: flex-end; flex-wrap: wrap; gap: var(--br-space-2); padding-top: var(--br-space-2); }
    .agent-settings label { display: flex; flex: 1 1 10rem; min-width: 0; flex-direction: column; gap: var(--br-space-1); font-size: var(--br-font-size-xs); color: var(--br-color-muted); }
    .reset { align-self: flex-end; }
  `],
})
export class ConversationAgentsComponent {
  @Input() agents: readonly ConversationAgentOption[] = [];
  @Input() settings: readonly SessionSettings[] = [];
  @Input() models: readonly ComposerModelOption[] = [];
  @Input() defaultModelLabel = 'Default';
  @Input() defaultEffortLabel = 'Default';
  @Output() settingsChanged = new EventEmitter<SessionSettings>();
  readonly efforts = composerEffortPlaceholder;

  setting(agentKey: string): SessionSettings {
    return this.settings.find(s => s.agentKey === agentKey) ?? { agentKey, model: null, effort: null };
  }
  hasCustomModel(agentKey: string): boolean {
    const model = this.setting(agentKey).model;
    return !!model && !this.models.some(m => (m.model || m.id) === model);
  }
  modelLabel(agentKey: string): string {
    const model = this.setting(agentKey).model;
    return model ? this.models.find(m => (m.model || m.id) === model)?.label || model : this.defaultModelLabel;
  }
  effortLabel(agentKey: string): string {
    const effort = this.setting(agentKey).effort;
    return effort ? this.efforts.find(e => e.value === effort)?.label || effort : this.defaultEffortLabel;
  }
  changeModel(agentKey: string, model: string | null): void {
    this.settingsChanged.emit({ ...this.setting(agentKey), model });
  }
  changeEffort(agentKey: string, effort: ReasoningEffort | null): void {
    this.settingsChanged.emit({ ...this.setting(agentKey), effort });
  }
  reset(agentKey: string): void { this.settingsChanged.emit({ agentKey, model: null, effort: null }); }
  @Output() mention = new EventEmitter<ConversationAgentOption>();
  @Output() returnToConversation = new EventEmitter<void>();
}
